// app/frontend/pages/Pricing/Pricing.tsx
import { useState } from "react";
import { Box, Container, Stack, Group, SimpleGrid, List, ThemeIcon, Badge } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconCheck, IconAlertCircle, IconSparkles } from "@tabler/icons-react";
import { L2KH1, L2KH2, L2KText } from "../../components/ui/Typography";
import { L2KCard } from "../../components/ui/Cards";
import { L2KButton } from "../../components/ui/Forms";
import { SEO } from "../../components/common/SEO";

interface Plan {
  id: string;
  name: string;
  price: number;
  price_formatted: string;
  interval: string;
  features: string[];
  savings?: string;
  popular?: boolean;
}

interface PricingPageProps {
  plans: {
    monthly: Plan;
    annual: Plan;
  };
  current_plan: string | null;
  subscription_active: boolean;
}

export default function Pricing({ plans, current_plan, subscription_active }: PricingPageProps) {
  const [loadingPlan, setLoadingPlan] = useState<string | null>(null);

  const handleSelectPlan = async (planId: string) => {
    if (subscription_active) {
      notifications.show({
        color: "blue",
        title: "Already Subscribed",
        message: "You already have an active subscription. Manage it from your billing settings.",
        autoClose: 5000,
      });
      return;
    }

    setLoadingPlan(planId);

    const csrfToken = document.querySelector<HTMLMetaElement>(
      'meta[name="csrf-token"]'
    )?.content;

    try {
      const response = await fetch("/subscriptions/checkout", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": csrfToken || "",
        },
        credentials: "include",
        body: JSON.stringify({ plan: planId }),
      });

      const data = await response.json();

      if (response.ok && data.checkout_url) {
        // Redirect to Stripe Checkout
        window.location.href = data.checkout_url;
      } else {
        notifications.show({
          color: "red",
          title: "Checkout Error",
          message: data.error || "Unable to start checkout",
          icon: <IconAlertCircle />,
          autoClose: 5000,
        });
      }
    } catch (error) {
      notifications.show({
        color: "red",
        title: "Error",
        message: "Something went wrong. Please try again.",
        icon: <IconAlertCircle />,
        autoClose: 5000,
      });
    } finally {
      setLoadingPlan(null);
    }
  };

  return (
    <Box
      style={{
        minHeight: "100vh",
        background: `
          radial-gradient(ellipse at 20% 50%, rgba(255, 152, 0, 0.15) 0%, transparent 50%),
          radial-gradient(ellipse at 80% 20%, rgba(255, 193, 7, 0.12) 0%, transparent 50%),
          radial-gradient(ellipse at 40% 80%, rgba(255, 87, 34, 0.1) 0%, transparent 50%),
          linear-gradient(135deg, #171717 0%, #1a1a1a 25%, #171717 50%, #1f1f1f 75%, #171717 100%)
        `,
        paddingTop: "60px",
        paddingBottom: "60px",
      }}
    >
      <SEO
        title="Pricing — Choose Your Urbankiz Learning Plan | Learntokiz"
        description="Pick the Learntokiz plan that fits your dance journey. All tiers include full course access, structured learning tracks, and community."
      />

      <Container size="lg">
        <Stack gap="xl" align="center">
          {/* Header */}
          <Stack gap="sm" align="center" ta="center" maw={600}>
            <L2KH1 variant="accent">Choose Your Plan</L2KH1>
            <L2KText variant="secondary" size="lg">
              Unlock your full dance potential with unlimited access to tutorials,
              the dance dictionary, and exclusive content.
            </L2KText>
          </Stack>

          {/* Plans */}
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xl" style={{ width: "100%", maxWidth: 900 }}>
            {/* Monthly Plan */}
            <PlanCard
              plan={plans.monthly}
              isCurrentPlan={current_plan === "monthly"}
              isActive={subscription_active}
              isLoading={loadingPlan === "monthly"}
              onSelect={() => handleSelectPlan("monthly")}
            />

            {/* Annual Plan */}
            <PlanCard
              plan={plans.annual}
              isCurrentPlan={current_plan === "annual"}
              isActive={subscription_active}
              isLoading={loadingPlan === "annual"}
              onSelect={() => handleSelectPlan("annual")}
            />
          </SimpleGrid>

          {/* FAQ / Trust */}
          <Stack gap="xs" align="center" mt="xl">
            <L2KText variant="secondary" size="sm">
              Cancel anytime. No questions asked.
            </L2KText>
            <L2KText variant="tertiary" size="xs">
              Secure payment powered by Stripe
            </L2KText>
          </Stack>
        </Stack>
      </Container>
    </Box>
  );
}

interface PlanCardProps {
  plan: Plan;
  isCurrentPlan: boolean;
  isActive: boolean;
  isLoading: boolean;
  onSelect: () => void;
}

function PlanCard({ plan, isCurrentPlan, isActive, isLoading, onSelect }: PlanCardProps) {
  const isPopular = plan.popular;

  return (
    <L2KCard
      variant="elevated"
      glow={isPopular}
      style={{
        position: "relative",
        border: isPopular ? "2px solid #FF9800" : undefined,
        transform: isPopular ? "scale(1.02)" : undefined,
      }}
    >
      {isPopular && (
        <Badge
          leftSection={<IconSparkles size={14} />}
          style={{
            position: "absolute",
            top: -12,
            left: "50%",
            transform: "translateX(-50%)",
            background: "linear-gradient(135deg, #FF9800, #FF5722)",
            color: "#fff",
            fontFamily: "'Public Sans', sans-serif",
            fontWeight: 600,
            padding: "4px 16px",
          }}
        >
          Most Popular
        </Badge>
      )}

      <Stack gap="lg" pt={isPopular ? "md" : 0}>
        {/* Plan Header */}
        <Stack gap="xs" align="center">
          <L2KH2>{plan.name}</L2KH2>
          <Group gap="xs" align="baseline">
            <span
              style={{
                fontSize: "48px",
                fontWeight: 700,
                fontFamily: "'Public Sans', sans-serif",
                color: "#F0F0F0",
                lineHeight: 1,
              }}
            >
              ${plan.price}
            </span>
            <L2KText variant="secondary">/{plan.interval}</L2KText>
          </Group>
          {plan.savings && (
            <Badge
              color="green"
              variant="light"
              style={{
                fontFamily: "'Public Sans', sans-serif",
              }}
            >
              {plan.savings}
            </Badge>
          )}
        </Stack>

        {/* Features */}
        <List
          spacing="sm"
          center
          icon={
            <ThemeIcon
              size={24}
              radius="xl"
              style={{
                background: "linear-gradient(135deg, rgba(255, 152, 0, 0.2), rgba(255, 87, 34, 0.2))",
                border: "1px solid rgba(255, 152, 0, 0.3)",
              }}
            >
              <IconCheck size={14} color="#FF9800" />
            </ThemeIcon>
          }
        >
          {plan.features.map((feature, index) => (
            <List.Item
              key={index}
              style={{
                color: "#D0D0D0",
                fontFamily: "'Public Sans', sans-serif",
                fontSize: "14px",
              }}
            >
              {feature}
            </List.Item>
          ))}
        </List>

        {/* CTA Button */}
        <L2KButton
          fullWidth
          size="lg"
          onClick={onSelect}
          loading={isLoading}
          disabled={isActive && isCurrentPlan}
          variant={isPopular ? "filled" : "outline"}
        >
          {isActive && isCurrentPlan
            ? "Current Plan"
            : isActive
            ? "Switch Plan"
            : "Get Started"}
        </L2KButton>
      </Stack>
    </L2KCard>
  );
}
